// External
/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import { func, object } from 'prop-types';
import { Grid, GridItem } from '@nike/nike-design-system-components';
import { NBY } from '@nike/nike-design-system-icons';
import { connect } from 'react-redux';
import styled from '@emotion/styled';

// Components
import { ORDER_TYPE_NAMES, ORDER_TYPE_DISPLAY_NAMES, CATALOG_STATUS } from '../constants';

// Functions
import { getCatalogTypesData } from '../../catalogTypes/selectors';

// Styles
import { StepBox } from '../../../styles/styles';
import { formatDateTimestamp, formatToDateAndTime, rangeFormatter } from '../../../util/date';

import Truncator from '../../../components/Truncator';

const marginTopTwelve = 'margin-top: 12px;';

const { ATONCE, FUTURES, FORECAST } = ORDER_TYPE_NAMES;

const truncatorProps = { withToolTip: true, toolTipPosition: 'right', enableRecalc: true };

// Modify accordingly
export const TextControlled = styled.div`
  text-decoration: ${({ textDecoration }) => textDecoration};
  text-underline-position: under;
  font-weight: ${({ fontWeight }) => fontWeight};
  margin-top: ${({ marginTop }) => marginTop};
  margin-bottom: ${({ marginBottom }) => marginBottom};
`;

export const CatalogsEditReadOnly = ({
  catalogTypesData,
  setHasClickedEditIcon,
  catalog: {
    atOnceEnd,
    atOnceStart,
    catalogTypes,
    directShip,
    forecastCrd,
    forecastEnd,
    forecastStart,
    futuresCrd,
    futuresEnd,
    futuresStart,
    groupName,
    id,
    indexedTimestamp,
    name,
    orderTypes,
    owner,
    salesOrg,
    seasons,
    status
  }
}) => {
  const doesOrderTypesInclude = orderType => orderTypes?.includes(orderType);

  const isDeleted = status === CATALOG_STATUS.DELETED;

  return (
    <>
      <Grid rowGap='12px' columnGap='70px'>
        <GridItem xs={1}>
          {!isDeleted && (
            <label htmlFor='catalogEditIcon' aria-label='catalogs-edit-icon'>
              <button type='button' data-testid='catalogEditIcon' id='catalogEditIcon' onClick={setHasClickedEditIcon}>
                <NBY />
              </button>
            </label>
          )}
        </GridItem>

        <GridItem xs={3}>
          <StepBox>General Information</StepBox>
          <TextControlled marginTop='4px'>{`ID: ${id?.substr(0, 7)}`}</TextControlled>
          <TextControlled marginTop='4px'>
            <Truncator value={`Name: ${name}`} {...truncatorProps} />
          </TextControlled>
          <TextControlled marginTop='4px'>
            <Truncator value={`Group Name: ${groupName}`} {...truncatorProps} />
          </TextControlled>
          <TextControlled marginTop='4px'>
            <Truncator value={`Sales Org: ${salesOrg}`} {...truncatorProps} />
          </TextControlled>
          <TextControlled marginTop='4px'>
            <Truncator
              value={`Catalog Type: ${catalogTypes
                ?.map(catalogType => catalogTypesData[catalogType]?.name)
                .join(', ')}`}
              {...truncatorProps}
            />
          </TextControlled>
          <TextControlled marginTop='4px'>
            <Truncator value={`Last Indexed: ${formatToDateAndTime(indexedTimestamp)}`} {...truncatorProps} />
          </TextControlled>
        </GridItem>

        <GridItem xs={2}>
          <StepBox>Order Types</StepBox>
          <TextControlled>
            {orderTypes?.map(orderType => ORDER_TYPE_DISPLAY_NAMES[orderType]).join(', ')}
          </TextControlled>
        </GridItem>

        <GridItem xs={3}>
          {doesOrderTypesInclude(ATONCE) && (
            <>
              <StepBox>At-Once Dates</StepBox>
              <TextControlled>{rangeFormatter({ start: atOnceStart, end: atOnceEnd })}</TextControlled>
            </>
          )}

          {doesOrderTypesInclude(FUTURES) && (
            <div
              css={css`
                ${doesOrderTypesInclude(ATONCE) && marginTopTwelve}
              `}
            >
              <StepBox>Futures Dates</StepBox>
              <TextControlled marginBottom='10px'>
                {rangeFormatter({ start: futuresStart, end: futuresEnd })}
              </TextControlled>
              <StepBox>Futures CRD</StepBox>
              <TextControlled marginTop='4px'>{formatDateTimestamp(futuresCrd)}</TextControlled>
            </div>
          )}

          {doesOrderTypesInclude(FORECAST) && (
            <div
              css={css`
                ${doesOrderTypesInclude(ATONCE) && marginTopTwelve}
              `}
            >
              <StepBox>Forecast Dates</StepBox>
              <TextControlled marginBottom='10px'>
                {rangeFormatter({ start: forecastStart, end: forecastEnd })}
              </TextControlled>
              <StepBox>Forecast CRD</StepBox>
              <TextControlled marginTop='4px'>{formatDateTimestamp(forecastCrd)}</TextControlled>
            </div>
          )}
        </GridItem>

        <GridItem xs={3}>
          <StepBox>{seasons?.length === 1 ? 'Season' : 'Seasons'}</StepBox>
          <TextControlled>{seasons?.join(', ')}</TextControlled>

          <div
            css={css`
              ${marginTopTwelve}
            `}
          >
            <StepBox>Direct Ship</StepBox>
            <TextControlled marginTop='4px'>{directShip ? 'Yes' : 'No'}</TextControlled>
          </div>

          <div
            css={css`
              ${marginTopTwelve}
            `}
          >
            <StepBox>Owner</StepBox>
            <TextControlled marginTop='4px'>{owner}</TextControlled>
          </div>
        </GridItem>
      </Grid>
    </>
  );
};

CatalogsEditReadOnly.propTypes = {
  catalog: object.isRequired,
  catalogTypesData: object.isRequired,
  setHasClickedEditIcon: func.isRequired,
  products: object.isRequired
};

const mapStateToProps = state => ({
  catalogTypesData: getCatalogTypesData(state)
});

export default connect(mapStateToProps)(CatalogsEditReadOnly);
